import { Alert, Box, Paper, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveObjectReturnScan } from '../../domain/contractObjectReturns';
import useQuery from '../../hooks/useQuery';
import useRequest from '../../hooks/useRequest';

interface PendingObjectReturn {
    contractNr: number;
    itemDescription: string;
    amountRon: number;
    clientName: string;
}

interface ObjectReturnQueue {
    enabled: boolean;
    items: PendingObjectReturn[];
}

const ContractObjectReturnsPanel = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [scan, setScan] = useState('');
    const [scanError, setScanError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { data, reload } = useQuery<ObjectReturnQueue>({
        path: '/contracts/object-returns',
        method: 'GET',
        pollInterval: 10000,
    });

    const items = data?.items ?? [];

    useEffect(() => {
        if (data?.enabled && items.length > 0) {
            inputRef.current?.focus();
        }
    }, [data?.enabled, items.length]);

    const confirmReturn = async () => {
        const resolved = resolveObjectReturnScan(
            scan,
            items.map(item => item.contractNr),
        );
        if (!resolved.ok) {
            const errorKey =
                resolved.reason === 'not-pending'
                    ? 'contracts.objectReturnNotPending'
                    : 'contracts.objectReturnInvalidScan';
            setScanError(t(errorKey));
            return;
        }

        setSubmitting(true);
        setScanError(null);
        try {
            await request({
                path: `/contracts/object-returns/${resolved.contractNr}`,
                method: 'DELETE',
            });
            setScan('');
            reload(true);
        } catch {
            setScanError(t('contracts.objectReturnFailed'));
        } finally {
            setSubmitting(false);
            inputRef.current?.focus();
        }
    };

    if (!data?.enabled || items.length === 0) {
        return null;
    }

    return (
        <Paper
            data-testid="contract-object-returns"
            elevation={10}
            sx={{
                position: 'fixed',
                right: { xs: 16, md: 360 },
                bottom: 24,
                width: { xs: 'calc(100vw - 32px)', sm: 360 },
                maxWidth: 360,
                zIndex: 1100,
                p: 1.5,
                color: 'common.white',
                backgroundColor: 'error.dark',
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('contracts.objectReturnsTitle')}
            </Typography>
            <Box
                sx={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    p: 1,
                    mb: 1,
                    color: 'text.primary',
                    backgroundColor: 'common.white',
                    borderRadius: 1,
                }}
            >
                {items.map(item => (
                    <Typography
                        key={item.contractNr}
                        variant="body2"
                        data-contract-nr={item.contractNr}
                    >
                        {`${item.contractNr} :: ${item.itemDescription} :: ${Number(item.amountRon).toFixed(2)} Lei :: ${item.clientName}`}
                    </Typography>
                ))}
            </Box>
            {scanError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {scanError}
                </Alert>
            )}
            <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                value={scan}
                disabled={submitting}
                placeholder={t('contracts.objectReturnScanPlaceholder')}
                onChange={event => setScan(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        void confirmReturn();
                    }
                }}
                slotProps={{ htmlInput: { 'aria-label': t('contracts.objectReturnScanLabel') } }}
                sx={{ backgroundColor: 'common.white', borderRadius: 1 }}
            />
        </Paper>
    );
};

export default ContractObjectReturnsPanel;
